import { Divider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useMatch } from 'react-router';

import {
  BaseThreadFragmentFragmentGQLType,
  GetEventLogsQueryGQLType,
  ThreadActionGQLType,
  LimitedPartnerEntityMetaFragmentFragmentGQLType,
  EventLogTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionsFn } from '~/core/PermissionGuard';
import { Thread } from '~/packages/comments/Thread';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { UserDetail } from '~/packages/common-frontend/components/UserDetail';
import { EntityName as BaseEntityName } from '~/packages/event-logs/EntityName';
import { EventFeedItem as BaseEventFeedItem } from '~/packages/event-logs/EventFeedItem';
import {
  EventLogFilter,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { useGetLimitedPartnerFiltersQuery } from '~/packages/event-logs/queries/entityFilters.gql.generated';
import { EventLogExportDataRow } from '~/packages/event-logs/types';

// Constants
export const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'limitedPartner_create',
  'limitedPartner_mapping_match',
  'limitedPartner_mapping_unmatch',
  'limitedPartner_thread_create',
  'limitedPartner_update',
];

// Types
export type EntityMeta = LimitedPartnerEntityMetaFragmentFragmentGQLType;
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type ThreadData = BaseThreadFragmentFragmentGQLType;

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  if (context.user.userType !== 'CAPITAL_PROVIDER') return 'deny';
  return context.flags.productsLpDashboardPermissions;
};

type EntityNameProps = Pick<EntityMeta, 'limitedPartnerId'> & {
  name?: string;
};
export const EntityName = ({ limitedPartnerId, name }: EntityNameProps) => {
  const match = useMatch({
    path: '/limited-partners/details/:limitedPartnerId',
    end: true,
  });

  const isOnLimitedPartnerRoute =
    match?.params.limitedPartnerId === limitedPartnerId;

  const resolvedName = name
    ? `${name} Limited Partner`
    : `Limited Partner ...${limitedPartnerId.slice(-12)}`;
  return (
    <BaseEntityName
      to={`/limited-partners/details/${limitedPartnerId}`}
      isOnEntityRoute={isOnLimitedPartnerRoute}
      label={!isOnLimitedPartnerRoute ? resolvedName : 'Limited Partner'}
      tooltipLabel={
        <>
          Limited Partner ID: {limitedPartnerId}
          {name ? (
            <>
              <br />
              Limited Partner Name: {name}
            </>
          ) : null}
        </>
      }
    />
  );
};

type ThreadFeedItemProps = {
  thread: ThreadData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const ThreadFeedItem = ({
  thread,
  onThreadAction,
  onMount,
  onReady,
}: ThreadFeedItemProps) => {
  switch (thread.domain) {
    case 'limitedPartner': {
      return (
        <Thread
          key={thread.threadId}
          threadId={thread.threadId}
          onThreadAction={onThreadAction}
          onReady={() => onReady?.(thread.threadId)}
          onMount={() => onMount?.(thread.threadId)}
        >
          <Typography inline variant="detail-muted">
            Thread on{' '}
            <EntityName
              limitedPartnerId={thread.domainIdentifier}
              // !TODO: switch to entity metadata
              // name={thread.entityMetadata.name}
            />
          </Typography>
          <Divider />
        </Thread>
      );
    }
    default: {
      return null;
    }
  }
};

type EventFeedItemProps = {
  log: EventLogData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const EventFeedItem = ({
  log,
  onThreadAction,
  onMount,
  onReady,
}: EventFeedItemProps) => {
  switch (log.__typename) {
    case 'LimitedPartnerThreadCreateEvent': {
      return (
        <Thread
          key={log.eventId}
          threadId={log.current.id}
          onThreadAction={onThreadAction}
          onMount={() => onMount?.(log.current.id)}
          onReady={() => onReady?.(log.current.id)}
        >
          <Typography inline variant="detail-muted">
            Thread on <EntityName {...log.entityMetadata} />
          </Typography>
          <Divider />
        </Thread>
      );
    }

    case 'LimitedPartnerCreatedEvent': {
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} /> created{' '}
          <EntityName {...log.entityMetadata} />
        </BaseEventFeedItem>
      );
    }

    case 'LimitedPartnerUpdatedEvent': {
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} /> updated{' '}
          <EntityName {...log.entityMetadata} />
        </BaseEventFeedItem>
      );
    }

    case 'LimitedPartnerMappingMatchEvent': {
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} /> matched{' '}
          <EntityName {...log.entityMetadata} /> to metadata.
          {/* Read snapshot from `log.current.matchMetadataId` when product decides what they want to show here */}
        </BaseEventFeedItem>
      );
    }

    case 'LimitedPartnerMappingUnmatchEvent': {
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} /> unmatched{' '}
          <EntityName {...log.entityMetadata} /> from metadata.
          {/* Read snapshot from `log.current.matchMetadataId` when product decides what they want to show here */}
        </BaseEventFeedItem>
      );
    }

    default: {
      return null;
    }
  }
};

export const EntityFilter = () => {
  const selectedIds = useEventLogFilters((s) => s.limitedPartnerIds);
  const onSelect = useEventLogFilters((s) => s.setLimitedPartnerIds);

  const { user } = useUserAuthOrThrow();
  const [searchString, setSearchString] = useState<string>('');

  const entityFiltersQuery = useGetLimitedPartnerFiltersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      organizationId: user.organizationId,
      queryString: searchString ?? undefined,
    },
  });

  // Show previous data while fetching new data (e.g. when searching)
  const data = entityFiltersQuery.data ?? entityFiltersQuery.previousData;
  const entities: EntityMeta[] = useMemo(
    () =>
      data?.limitedPartners.map((limitedPartner) => ({
        ...limitedPartner,
        limitedPartnerId: limitedPartner.id,
        __typename: 'LimitedPartnerEntityMeta',
      })) ?? [],
    [data?.limitedPartners],
  );

  const options = useMemo(
    () =>
      entities.map((entity) => {
        const textLabel = _.startCase(entity.name);
        return {
          id: entity.limitedPartnerId,
          textLabel:
            textLabel.length > 30 ? `${textLabel.slice(0, 30)}...` : textLabel,
          data: entity,
        };
      }),
    [entities],
  );

  if (entityFiltersQuery.error) {
    Sentry.captureException(entityFiltersQuery.error);
    return null;
  }
  if (entities.length < 1) return null;

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="circle-dollar-to-slot" />}
      minWidth="13.5rem"
      noneLabel="Any Limited Partner"
      singularLabel="Limited Partner"
      pluralLabel="Limited Partners"
      selectedIds={selectedIds}
      options={options}
      onClose={onSelect}
    />
  );
};

/* eslint-disable camelcase */
export const toExportDataRow = (
  dataRow: EventLogExportDataRow,
  log: EventLogData,
): EventLogExportDataRow => {
  switch (log.__typename) {
    case 'LimitedPartnerThreadCreateEvent': {
      return {
        ...dataRow,
        limitedPartner_meta_name: log.entityMetadata.name,
      };
    }

    case 'LimitedPartnerCreatedEvent':
    case 'LimitedPartnerUpdatedEvent': {
      const nextDataRow = {
        ...dataRow,
        limitedPartner_meta_name: log.entityMetadata.name,
        limitedPartner_status_name: log.current.name,
        limitedPartner_status_otherNames: log.current.otherNames?.join(','),
        limitedPartner_status_ownerOrganizationId:
          log.current.ownerOrganizationId,
      };
      if (log.current.parentEntityId) {
        nextDataRow.limitedPartner_status_parentEntityId =
          log.current.parentEntityId;
      }

      return nextDataRow;
    }

    case 'LimitedPartnerMappingMatchEvent': {
      return {
        ...dataRow,
        limitedPartner_meta_name: log.entityMetadata.name,
        limitedPartner_match_matchMetadataId: log.current.matchMetadataId,
        limitedPartner_match_mappingId: log.current.mappingId,
      };
    }

    case 'LimitedPartnerMappingUnmatchEvent': {
      return {
        ...dataRow,
        limitedPartner_meta_name: log.entityMetadata.name,
        limitedPartner_match_matchMetadataId: log.previous.matchMetadataId,
        limitedPartner_match_mappingId: log.previous.mappingId,
      };
    }

    default: {
      return dataRow;
    }
  }
};
/* eslint-enable camelcase */
